import { z } from "zod";
import type { Enums } from "../../types";

/** The purposes a mailbox can be connected for; each is a feature that reads what arrives. */
export const MAILBOX_PURPOSES = ["incoming-invoices"] as const satisfies ReadonlyArray<
	Enums<"mailboxPurpose">
>;

/** How the IMAP connection is protected: implicit TLS on 993, or STARTTLS on 143. */
export const MAILBOX_IMAP_SECURITIES = ["tls", "starttls"] as const satisfies ReadonlyArray<
	Enums<"mailboxImapSecurity">
>;

/** The polling intervals the settings tab offers, in minutes. */
export const MAILBOX_POLLING_INTERVALS = [5, 15, 30, 60] as const;

/**
 * A new mailbox: everything the connection needs, password included. The password is
 * write-only — no route ever returns it — and the server verifies the login before storing
 * anything, so a mailbox that exists is one that opened once.
 */
export const mailboxSchema = z.object({
	purpose: z.enum(MAILBOX_PURPOSES),
	label: z.string().trim().min(1).max(80),
	emailAddress: z.email().trim().toLowerCase(),
	folder: z.string().trim().min(1).max(200),
	imapHost: z.string().trim().min(1).max(253),
	imapPort: z.number().int().min(1).max(65535),
	imapSecurity: z.enum(MAILBOX_IMAP_SECURITIES),
	imapUsername: z.string().trim().min(1).max(320),
	password: z.string().min(1).max(1024),
	pollingIntervalMinutes: z.number().int().min(1).max(1440),
});

export type MailboxSchema = z.infer<typeof mailboxSchema>;

/**
 * An edit: the same fields without the purpose, which a mailbox keeps for life, and with the
 * password optional — an absent or empty field means "unchanged", a filled one is verified and
 * replaces the stored secret.
 */
export const patchMailboxSchema = mailboxSchema.omit({ purpose: true, password: true }).extend({
	password: z.string().max(1024).optional(),
	isPaused: z.boolean(),
});

export type PatchMailboxSchema = z.infer<typeof patchMailboxSchema>;
